import { Box } from "@mui/material";
import { sortByKey } from "@notemeal/utils-sort";
import { useHasFeature } from "shared-ui/Feature";
import { newServingAmount } from "shared-ui/ServingAmount/utils";
import { useBrowserBackAndRefreshWarning } from "apps/web/src/hooks/useBrowserBackAndRefreshWarning";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import React, { useReducer, useRef, useState } from "react";
import { ConfirmationDialog } from "../../componentLibrary";
import { useEnvironmentType } from "../../contexts/useEnvType";
import { NAV_KITCHEN_MENU_ITEMS, NAV_ORG_KITCHEN } from "../../pages/Auth/Org/Kitchen/KitchenPaths";
import { trackEvent } from "../../reporting/reporting";
import {
  FullServingAmountFragment,
  GptRecipeFragment,
  RecipeFullFragment,
  useCreateRecipeMutation,
  useEditRecipeAllergensMutation,
  useCreateReusableMenuItemMutation,
} from "../../types";
import MenuItemDialog from "../MenuItem/Dialog/MenuItemDialog";
import { buildRecipeMenuItemInitState } from "../MenuItem/Dialog/recipeToMenuItemInitState";
import { onCreate as onCreateMenuItem } from "../MenuItem/Dialog/utils";
import { MenuItemState } from "../MenuItem/reducer";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import LoadingBackdrop from "../universal/LoadingBackdrop";
import { selfAllergensInput } from "./Form/allergens";
import RecipeForm from "./Form/RecipeForm";
import {
  RecipeFormState,
  canSaveRecipeFormTooltips,
  newRecipeFormState,
  newRecipeFormStateFromGPTRecipe,
  recipeFormReducer,
  recipeFormToInput,
  recipeServingFormToInput,
} from "./Form/utils";
import { GenerateWithGPTDialog } from "./GPT/Generator/GenerateWithGPTDialog";
import { TranscribeWithGPTDialog } from "./GPT/Transcriber/TranscribeWithGPTDialog";
import { TranscribeError, getUserFriendlyMessage } from "./GPT/Transcriber/utils";
import { CreateRecipeFooter } from "./Header/CreateRecipeFooter";
import { CreateRecipeHeader } from "./Header/CreateRecipeHeader";
import { RecipeScaleDialog } from "./Scale/RecipeScaleDialog";
import RecipeScaleSuccessAlert from "./Scale/RecipeScaleSuccessAlert";
interface RecipeCreateDialogProps {
  onClose: () => void;
  onCreate: (recipe: RecipeFullFragment) => void;
  initialIngredients?: readonly FullServingAmountFragment[];
  initialName?: string;
  initialRecipeState?: RecipeFormState;
  forStaff?: boolean;
  setShowCreateRecipeModal?: (show: boolean) => void;
  isSuccessAlert?: boolean;
  dialogView?: boolean;
}

const RecipeCreateContent = ({
  onClose,
  onCreate,
  initialIngredients,
  initialName,
  initialRecipeState,
  forStaff,
  setShowCreateRecipeModal,
  isSuccessAlert = false,
  dialogView,
}: RecipeCreateDialogProps) => {
  const [state, dispatch] = useReducer(recipeFormReducer, initialRecipeState || newRecipeFormState(initialName, initialIngredients));

  const _hasPerformanceKitchenFeatureFlag = useHasFeature("performanceKitchen", { skip: forStaff });
  const { setMessage } = useSnackbar();
  const { isCommercial } = useEnvironmentType();
  const { isGetRecipeFromUrlEnabled, isRecipeAndMenuItemAllergensEnabled, useBedrock } = useNutritionFlags();
  const [transcriberError, setTranscriberError] = useState<TranscribeError | null>(null);
  const { setBrowserBackAndRefreshWarningEnabled } = useBrowserBackAndRefreshWarning();
  const showAIRecipes = !forStaff && (isCommercial || useBedrock);
  const [showScaleSuccessMessage, setShowScaleSuccessMessage] = useState(isSuccessAlert);
  const [openScaleRecipe, setOpenScaleRecipe] = useState(false);
  const [openRecipeUnsavedChangesDialog, setOpenRecipeUnsavedChangesDialog] = useState(false);
  const initialFormState = useRef<RecipeFormState>(state);
  const [showGenerateRecipeModal, setShowGenerateRecipeModal] = useState(false);
  const [showTranscribePhotoModal, setShowTranscribePhotoModal] = useState(false);
  const [showImportLinkModal, setShowImportLinkModal] = useState(false);
  const pendingSaveAsMenuItemRef = useRef(false);
  const pendingCreatedRecipeRef = useRef<RecipeFullFragment | null>(null);
  const pendingCreateMenuItemNameRef = useRef("");
  const [menuItemInitState, setMenuItemInitState] = useState<MenuItemState | null>(null);

  const onGenerateError = (error: any) => {
    const userFriendlyMessage = getUserFriendlyMessage(error);
    setMessage("error", userFriendlyMessage);
  };

  const onGPTRecipeComplete = (gptRecipe: GptRecipeFragment, closeModal: () => void) => {
    const newState = newRecipeFormStateFromGPTRecipe(gptRecipe);
    dispatch({
      type: "REPLACE_STATE",
      payload: newState,
    });
    closeModal();
    setShowTranscribePhotoModal(false);
    setShowImportLinkModal(false);

    setShowCreateRecipeModal && setShowCreateRecipeModal(true);
  };

  const [createReusableMenuItem, { loading: savingMenuItem }] = useCreateReusableMenuItemMutation({
    onError: () => {
      setMessage("error", `Unable to create ${pendingCreateMenuItemNameRef.current} as a menu item`);
    },
    onCompleted: data => {
      const created = data.createReusableMenuItem.menuItem;
      trackEvent("nutrition_recipe_saved_as_menu_item", {});
      const recipe = pendingCreatedRecipeRef.current;
      pendingSaveAsMenuItemRef.current = false;
      pendingCreatedRecipeRef.current = null;
      setMenuItemInitState(null);
      setMessage("success", `${created.name} has been saved as a menu item`, {
        label: "View Menu Items",
        to: `${NAV_ORG_KITCHEN}/${NAV_KITCHEN_MENU_ITEMS}`,
      });
      if (recipe) {
        onCreate(recipe);
      }
    },
  });

  const [editRecipeAllergens] = useEditRecipeAllergensMutation();
  const pendingSelfAllergensRef = useRef<string[] | null>(null);

  // Creating a recipe has no allergen input, so manual allergens are saved on the new recipe right after
  const saveAllergens = async (recipe: RecipeFullFragment): Promise<RecipeFullFragment> => {
    const selfAllergens = pendingSelfAllergensRef.current;
    if (!selfAllergens) {
      return recipe;
    }
    try {
      const { data } = await editRecipeAllergens({ variables: { input: { id: recipe.id, selfAllergens } } });
      const saved = data?.editRecipeAllergens.recipe;
      return saved ? { ...recipe, allergens: saved.allergens, selfAllergens: saved.selfAllergens } : recipe;
    } catch {
      setMessage("error", `${recipe.name} was created but its allergens could not be saved`);
      return recipe;
    }
  };

  const [createRecipe, { loading: savingRecipe }] = useCreateRecipeMutation({
    onError: () => {
      pendingSaveAsMenuItemRef.current = false;
      pendingCreatedRecipeRef.current = null;
      setMessage("error", `Unable to create ${state.name} as a recipe`);
    },
    onCompleted: async data => {
      const recipe = await saveAllergens(data.createRecipeFull.recipe);
      trackEvent("nutrition_recipe_created", {
        mealType: state.mealTypes.map(mealType => mealType.toLowerCase()).join(","),
      });
      if (!pendingSaveAsMenuItemRef.current) {
        onCreate(recipe);
        return;
      }
      const defaultServing = recipe.servings.find(s => s.isDefault) ?? recipe.servings[0];
      if (!defaultServing) {
        pendingSaveAsMenuItemRef.current = false;
        onCreate(recipe);
        return;
      }
      pendingCreatedRecipeRef.current = recipe;
      const previewImage = state.images.length ? sortByKey(state.images, "position")[0] : null;
      const servingAmount = newServingAmount(defaultServing, 1);
      setMenuItemInitState(
        buildRecipeMenuItemInitState({
          id: recipe.id,
          name: recipe.name,
          imageUrl: previewImage?.url ?? null,
          servingAmounts: [servingAmount],
        })
      );
    },
    update: cache => {
      cache.evict({
        fieldName: "recipeOffsetConnection",
        broadcast: true,
      });
    },
  });

  const handleMenuItemDialogSave = (menuItemState: MenuItemState) => {
    pendingCreateMenuItemNameRef.current = menuItemState.menuItem.name;
    onCreateMenuItem({ createReusableMenuItem, state: menuItemState });
  };

  const handleMenuItemDialogClose = () => {
    const recipe = pendingCreatedRecipeRef.current;
    pendingSaveAsMenuItemRef.current = false;
    pendingCreatedRecipeRef.current = null;
    setMenuItemInitState(null);
    if (recipe) {
      onCreate(recipe);
    }
  };

  const handleSave = () => {
    dispatch({
      type: "CHANGE_RECIPE_SAVE_CLICK",
      payload: true,
    });
    if (state.isAiTranscribed) {
      trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Save Transcribed Recipe", {});
    }
    const recipeInput = recipeFormToInput(state);
    if (canSaveTooltips.length || !recipeInput) {
      pendingSaveAsMenuItemRef.current = false;
      return;
    }
    setBrowserBackAndRefreshWarningEnabled(false);
    pendingSelfAllergensRef.current = isRecipeAndMenuItemAllergensEnabled ? selfAllergensInput(state) : null;
    createRecipe({
      variables: {
        input: {
          recipe: recipeInput,
          servings: [state.serving].map(recipeServingFormToInput),
          copiedFromId: initialRecipeState?.id || null,
        },
      },
    });
  };

  React.useEffect(() => {
    setBrowserBackAndRefreshWarningEnabled(state.edited);
  }, [state.edited, setBrowserBackAndRefreshWarningEnabled]);

  const [discardChangesOpen, setDiscardChangesOpen] = useState(false);

  const handleClose = () => {
    setBrowserBackAndRefreshWarningEnabled(false);
    if (state.edited) {
      setDiscardChangesOpen(true);
    } else {
      onClose();
    }
  };

  const canSaveTooltips = canSaveRecipeFormTooltips(state);

  // Keep assisted methods reachable after AI apply — gate on eligibility only, not isAiGenerated/isAiTranscribed.
  const onAiGenerateClicked = () => {
    trackEvent("Nutrition | Food Management | Recipe | Enter AI Generate Flow", {});
    setShowGenerateRecipeModal(true);
  };
  const onAiTranscribeClicked = () => {
    trackEvent("Nutrition | Food Management | Recipe | Enter AI Transcription Flow", { type: "Photo" });
    setShowTranscribePhotoModal(true);
  };
  const onImportFromLinkClicked = () => {
    trackEvent("Nutrition | Food Management | Recipe | Enter AI Transcription Flow", { type: "URL" });
    setShowImportLinkModal(true);
  };
  const showSaveAsMenuItem = !forStaff && _hasPerformanceKitchenFeatureFlag && isCommercial;
  const onSaveAsMenuItemClicked = () => {
    pendingSaveAsMenuItemRef.current = true;
    handleSave();
  };

  return savingRecipe && !menuItemInitState ? (
    <LoadingBackdrop open={savingRecipe} onClose={onClose} />
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", m: dialogView ? 3 : 0, gap: 3 }}>
      <Box flexGrow={1} sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
        <CreateRecipeHeader
          forStaff={forStaff}
          isShared={state.isShared}
          onChange={() =>
            dispatch({
              type: "CHANGE_IS_SHARED",
              value: !state.isShared,
            })
          }
          handleClose={handleClose}
          handleSave={handleSave}
          canSaveTooltips={canSaveTooltips}
          onSaveAsMenuItemClicked={showSaveAsMenuItem ? onSaveAsMenuItemClicked : null}
        />
        <RecipeScaleSuccessAlert open={showScaleSuccessMessage} onClose={() => setShowScaleSuccessMessage(false)} />
        <RecipeForm
          forStaff={forStaff}
          state={state}
          dispatch={dispatch}
          onAiGenerateClicked={showAIRecipes ? onAiGenerateClicked : null}
          onAiTranscribeClicked={showAIRecipes ? onAiTranscribeClicked : undefined}
          onImportFromLinkClicked={showAIRecipes && isGetRecipeFromUrlEnabled ? onImportFromLinkClicked : undefined}
          openScaleRecipe={() => setOpenScaleRecipe(true)}
        />
      </Box>
      <CreateRecipeFooter
        forStaff={forStaff}
        isShared={state.isShared}
        onChange={() =>
          dispatch({
            type: "CHANGE_IS_SHARED",
            value: !state.isShared,
          })
        }
        handleClose={handleClose}
        handleSave={handleSave}
        canSaveTooltips={canSaveTooltips}
        onSaveAsMenuItemClicked={showSaveAsMenuItem ? onSaveAsMenuItemClicked : null}
      />
      <RecipeScaleDialog
        state={state}
        initialFormState={initialFormState.current}
        dispatch={dispatch}
        openScaleChangeDialog={openScaleRecipe}
        setOpenScaleChangeDialog={setOpenScaleRecipe}
        openRecipeUnsavedChangesDialog={openRecipeUnsavedChangesDialog}
        setOpenRecipeUnsavedChangesDialog={setOpenRecipeUnsavedChangesDialog}
        onSuccess={setShowScaleSuccessMessage}
        openScaleRecipe={value => setOpenScaleRecipe(value)}
      />
      {discardChangesOpen && (
        <ConfirmationDialog
          open={discardChangesOpen}
          title="Discard Unsaved Changes"
          message="Are you sure you want to go back? All unsaved changes will be discarded."
          onCancel={() => setDiscardChangesOpen(false)}
          onConfirm={() => {
            if (state.isAiTranscribed) {
              trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Discard Transcribed Recipe", {});
            }
            onClose();
          }}
          confirmLabel="Discard"
          variant="containedDestructive"
        />
      )}
      {showGenerateRecipeModal && (
        <GenerateWithGPTDialog
          open={showGenerateRecipeModal}
          onClose={() => setShowGenerateRecipeModal(false)}
          onGenerateComplete={onGPTRecipeComplete}
          onError={onGenerateError}
          initialName={state.name}
        />
      )}
      {showTranscribePhotoModal && (
        <TranscribeWithGPTDialog
          open={showTranscribePhotoModal}
          onClose={() => setShowTranscribePhotoModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="photo"
        />
      )}
      {showImportLinkModal && (
        <TranscribeWithGPTDialog
          open={showImportLinkModal}
          onClose={() => setShowImportLinkModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="link"
        />
      )}
      {menuItemInitState && (
        <MenuItemDialog
          open={true}
          onClose={handleMenuItemDialogClose}
          onSave={handleMenuItemDialogSave}
          title="Create Reusable Menu Item"
          variant="MenuItem"
          initState={menuItemInitState}
          saving={savingMenuItem}
          allowSaveAsRecipe={false}
        />
      )}
    </Box>
  );
};

export default RecipeCreateContent;
